import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { getArticleById } from '@/mocks/articleData';
import { hotRecommendations } from '@/mocks/homeData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = getArticleById(articleId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-8 py-20">
            <div className="max-w-7xl mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">内容未找到</h1>
              <p className="text-foreground-500 text-sm mb-6">您要查看的内容不存在或已下架</p>
              <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedVideos = hotRecommendations.slice(0, 4);

  const articleImageMap: Record<string, string> = {
    'movie-ride-on': 'https://readdy.ai/api/search-image?query=Chinese%20action%20movie%20scene%20with%20martial%20arts%20master%20and%20horse%2C%20warm%20sunset%20lighting%2C%20emotional%20atmosphere%2C%20cinematic&width=1200&height=600&seq=article-movie-ride-on&orientation=landscape',
    'variety-sisters-6': 'https://readdy.ai/api/search-image?query=Elegant%20Chinese%20women%20on%20grand%20stage%20with%20spotlights%2C%20music%20show%2C%20vibrant%20warm%20lighting%2C%20cinematic&width=1200&height=600&seq=article-variety-sisters-6&orientation=landscape',
    'tv-wind-rises-luoyang': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20city%20at%20night%20with%20lanterns%2C%20mysterious%20detective%20drama%2C%20warm%20golden%20atmosphere%2C%20cinematic&width=1200&height=600&seq=article-tv-wind-rises-luoyang&orientation=landscape',
    'movie-wandering-earth-3': 'https://readdy.ai/api/search-image?query=Epic%20Chinese%20sci-fi%20space%20scene%20with%20massive%20engines%2C%20dramatic%20lighting%2C%20warm%20orange%20and%20blue%20contrast&width=1200&height=600&seq=article-wandering-earth-3&orientation=landscape',
    'tv-fanhua': 'https://readdy.ai/api/search-image?query=1990s%20Shanghai%20night%20city%20with%20neon%20lights%2C%20nostalgic%20warm%20atmosphere%2C%20elegant%20cinematic&width=1200&height=600&seq=article-tv-fanhua&orientation=landscape',
    'variety-running-man': 'https://readdy.ai/api/search-image?query=Chinese%20outdoor%20variety%20show%20team%20in%20colorful%20urban%20setting%2C%20joyful%20sunny%20atmosphere&width=1200&height=600&seq=article-variety-running-man&orientation=landscape',
    'anime-nezha-2': 'https://readdy.ai/api/search-image?query=Chinese%20mythological%20anime%20hero%20with%20fire%20effects%2C%20epic%20battle%2C%20vibrant%20red%20and%20gold%2C%20cinematic&width=1200&height=600&seq=article-anime-nezha-2&orientation=landscape',
  };
  const articleImageUrl = articleImageMap[article.imageSeq] || articleImageMap['movie-ride-on'];

  const relatedImages: Record<string, string> = {
    'movie-wandering-earth-3': 'https://readdy.ai/api/search-image?query=Epic%20Chinese%20sci-fi%20space%20scene%2C%20cinematic%20movie%20poster%2C%20warm%20orange%20tones&width=600&height=350&seq=article-rel-wandering-earth&orientation=landscape',
    'movie-pegasus-3': 'https://readdy.ai/api/search-image?query=Racing%20car%20in%20desert%20at%20sunset%2C%20action%20movie%20shot%2C%20warm%20golden%20lighting&width=600&height=350&seq=article-rel-pegasus&orientation=landscape',
    'tv-fanhua': 'https://readdy.ai/api/search-image?query=1990s%20Shanghai%20night%20city%2C%20nostalgic%20warm%20atmosphere%2C%20elegant%20drama%20aesthetic&width=600&height=350&seq=article-rel-fanhua&orientation=landscape',
    'variety-running-man': 'https://readdy.ai/api/search-image?query=Chinese%20variety%20show%20outdoor%20team%2C%20joyful%20sunny%20urban%20scene&width=600&height=350&seq=article-rel-running-man&orientation=landscape',
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: `糖豆视频为您呈现${article.title}，来自${article.platform}的${article.category}内容`,
    datePublished: `${article.year}-01-01`,
    url: `https://www.tangdoushipin.com/article/${article.id}`,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={[
              { name: '首页', path: '/' },
              { name: article.category, path: `/category/${article.category === '电影' ? 'movie' : article.category === '电视剧' ? 'tv' : article.category === '综艺' ? 'variety' : article.category === '动漫' ? 'anime' : 'movie'}` },
              { name: article.title },
            ]} />

            {/* Article Header */}
            <article className="py-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.year}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.platform}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="flex items-center gap-1 text-xs text-accent-600">
                  <i className="ri-star-fill text-xs"></i>
                  {article.rating}
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {article.title}
              </h1>

              <div className="aspect-video rounded-xl overflow-hidden mb-8 bg-background-200">
                <img
                  src={articleImageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  title={article.title}
                />
              </div>

              <div className="flex items-center gap-5 pb-6 border-b border-background-200/70 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-play-circle-line text-primary-600 text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-700">{article.platform}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-star-fill text-accent-600 text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-700">{article.rating} 分</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-information-line text-secondary-600 text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-700">{article.updateInfo}</span>
                </div>
              </div>

              <div
                className="prose prose-sm max-w-none text-foreground-800 leading-relaxed mb-8"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex items-center gap-2 flex-wrap mb-10">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70 whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </article>

            {/* Related Content */}
            <section className="pb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-product-shop="">
                {relatedVideos.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    category={video.category}
                    platform={video.platform}
                    rating={video.rating}
                    year={video.year}
                    updateInfo={video.updateInfo}
                    imageUrl={relatedImages[video.imageSeq] || ''}
                    url={video.url}
                  />
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}